// Automations in WORDS — the one line shown on the sensor, on the light it
// drives, and in the location's list:
//
//   When 🚪 Bedroom door Open · 19:00–00:00 · if off → Night Lamp ON
//
// Pure, so the wording is tested, and the same everywhere. The rule itself is
// run by the server (Home Automation api/new/automations.js).

import { rfType } from '../devices/rfTypes';

export const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// Bit 0 = Sunday … bit 6 = Saturday, as the server stores Days. 0 = every day.
export const daysOf = (mask) => DAY_NAMES.filter((_, i) => Number(mask) & (1 << i));
export const toggleDay = (mask, i) => (Number(mask) || 0) ^ (1 << i);

export const COMMANDS = [
  { value: 'on', label: 'ON' },
  { value: 'off', label: 'OFF' },
  { value: 'toggle', label: 'Toggle' },
];
// Fan steps the firmware accepts (docs/flutter-reference: only these do anything).
export const FAN_STEPS = ['10', '20', '40', '60', '80', '100'];

const hm = (t) => (t ? String(t).slice(0, 5) : '');

export function commandLabel(cmd) {
  const c = String(cmd || '').toLowerCase();
  const hit = COMMANDS.find((x) => x.value === c);
  if (hit) return hit.label;
  return /^\d+$/.test(c) ? `fan ${c}%` : c;
}

/** "Bedroom door Open" / "Hall device says 'motion detected'". */
export function triggerText(rule, { sensors = [], messageDevices = [] } = {}) {
  if (String(rule.TriggerKind) === 'message') {
    const d = messageDevices.find((x) => String(x.UniqueNumber) === String(rule.TriggerUniqueNumber));
    const name = (d && (d.DeviceName || d.InputName)) || rule.TriggerUniqueNumber;
    return `${name} says “${rule.TriggerText}”`;
  }
  const s = sensors.find((x) => Number(x.RfDeviceId) === Number(rule.TriggerRfDeviceId))
    || (rule.Trigger && Number(rule.Trigger.RfDeviceId) === Number(rule.TriggerRfDeviceId) ? rule.Trigger : null);
  const icon = s ? rfType(s.RfType).icon : '📡';
  const name = (s && s.Name) || `sensor ${rule.TriggerRfDeviceId}`;
  return `${icon} ${name}${rule.TriggerMeaning ? ` ${rule.TriggerMeaning}` : ' (any event)'}`;
}

/** The "only if" part, as short phrases. */
export function conditionParts(rule) {
  const out = [];
  if (rule.TimeFrom && rule.TimeTo) out.push(`${hm(rule.TimeFrom)}–${hm(rule.TimeTo)}`);
  const days = daysOf(rule.Days);
  if (days.length && days.length < 7) out.push(days.join(' '));
  if (rule.ModeIs === 'locked') out.push('🔒 locked');
  if (rule.ModeIs === 'unlocked') out.push('🔓 unlocked');
  if (rule.OccupancyIs) out.push(`room ${rule.OccupancyIs}`);
  if (Number(rule.OnlyIfOff)) out.push('if off');
  if (Number(rule.CooldownMin) > 0) out.push(`once per ${rule.CooldownMin} min`);
  return out;
}

/** "Night Lamp ON, Fan fan 40%". */
export function actionsText(actions = []) {
  return actions.map((a) => {
    const name = String(a.OutputName || '').trim() || `${a.DeviceName || a.UniqueNumber} #${a.Output}`;
    return `${name} ${commandLabel(a.Command)}`;
  }).join(', ');
}

/** The whole rule in one line. */
export function describeAutomation(rule, ctx) {
  const when = triggerText(rule, ctx);
  const cond = conditionParts(rule);
  const off = Number(rule.OffAfterMin) > 0 ? ` (off after ${rule.OffAfterMin} min)` : '';
  return `When ${when}${cond.length ? ` · ${cond.join(' · ')}` : ''} → ${actionsText(rule.Actions || [])}${off}`;
}

/** A blank rule, optionally pre-filled from where the user came from. */
export function newAutomation({
  locationId, rfDeviceId = 0, meaning = '', uniqueNumber = '', output = 0,
} = {}) {
  return {
    AutomationId: 0,
    LocationID: Number(locationId) || 0,
    Name: '',
    Enabled: 1,
    TriggerKind: 'rf',
    TriggerRfDeviceId: Number(rfDeviceId) || 0,
    TriggerMeaning: meaning,
    TriggerUniqueNumber: '',
    TriggerText: '',
    TimeFrom: '',
    TimeTo: '',
    Days: 0,
    ModeIs: '',
    OccupancyIs: '',
    OnlyIfOff: 1,
    CooldownMin: 0,
    OffAfterMin: 0,
    Actions: uniqueNumber && output ? [{ UniqueNumber: String(uniqueNumber), Output: Number(output), Command: 'on' }] : [],
  };
}

/** What blocks saving, in words — or '' when the rule can be saved. */
export function validateAutomation(a) {
  if (a.TriggerKind === 'message') {
    if (!a.TriggerUniqueNumber) return 'Choose the device to listen to.';
    if (!String(a.TriggerText || '').trim()) return 'Type the text its message contains.';
  } else if (!Number(a.TriggerRfDeviceId)) return 'Choose the sensor.';
  if (!(a.Actions || []).some((x) => x.UniqueNumber && Number(x.Output) > 0)) return 'Add at least one button to switch.';
  if ((a.TimeFrom && !a.TimeTo) || (!a.TimeFrom && a.TimeTo)) return 'Set both times of the window, or neither.';
  if (a.TimeFrom && a.TimeFrom === a.TimeTo) return 'The window’s two times must differ.';
  return '';
}
